/**
 * Hub Tabs Component — Apex Design System v3.
 *
 * Reusable tab bar for hub pages. Features a gradient active indicator
 * with glow, smooth hover transitions, and icon tinting.
 */

interface HubTab {
  key: string;
  label: string;
  icon: React.ElementType;
}

interface HubTabsProps {
  tabs: HubTab[];
  activeTab: string;
  onTabChange: (key: string) => void;
  paramName?: string;
}

function HubTabs({ tabs, activeTab, onTabChange }: HubTabsProps) {
  return (
    <div className="border-b border-[var(--border-subtle)]">
      <nav className="flex gap-0.5 overflow-x-auto pb-px scrollbar-hide">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.key;
          return (
            <button
              key={tab.key}
              onClick={() => onTabChange(tab.key)}
              className={`group relative flex items-center gap-2 shrink-0 px-4 pb-3 pt-1.5 text-[13px] font-semibold transition-all duration-200 rounded-t-[var(--radius-md)] ${
                isActive
                  ? "text-[var(--color-interactive-primary)]"
                  : "text-[var(--text-tertiary)] hover:text-[var(--text-primary)] hover-gradient"
              }`}
            >
              <tab.icon
                className={`h-4 w-4 transition-all duration-200 ${
                  isActive
                    ? "text-[var(--color-interactive-primary)]"
                    : "opacity-50 group-hover:opacity-80"
                }`}
              />
              {tab.label}
              {/* Active indicator — gradient bar with glow */}
              {isActive && (
                <span
                  className="absolute bottom-0 left-2 right-2 h-[2.5px] rounded-full"
                  style={{
                    background:
                      "linear-gradient(90deg, oklch(62% 0.22 285), oklch(62% 0.20 320), oklch(62% 0.14 350))",
                    boxShadow:
                      "0 1px 6px oklch(62% 0.22 285 / 0.35), 0 0 16px oklch(62% 0.22 285 / 0.12)",
                  }}
                />
              )}
            </button>
          );
        })}
      </nav>
    </div>
  );
}

export default HubTabs;
